import { ClaimStatus, Loader, TitleCard } from '@percent/cause-dashboard/common/components'
import { useAuthState, usePermissionCheck, useQuery, useScrollToSection } from '@percent/cause-dashboard/common/hooks'
import { CLAIM_STATUS } from '@percent/cause-dashboard/constants/claimStatus'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { Flex, Grid, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { StringParam, useQueryParams } from 'use-query-params'
import styles from './ProfilePage.module.scss'
import { BankDetails } from './bankDetails/BankDetails'
import { ProfileForm } from './profileForm/ProfileForm'
import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'

export function ProfilePage() {
  const { t } = useTranslation()
  useBreadcrumbs([{ label: t('breadcrumb.profile') }])
  const [{ section }] = useQueryParams({ section: StringParam })

  const { causeService } = useServices()

  const {
    authState: { user, organisation, claimOrganisation }
  } = useAuthState()
  const verifiedCauseOwner = !!user?.organisationId
  const organisationId = organisation?.id ?? claimOrganisation?.id

  const [{ data: cause, isLoading }, { refresh }] = useQuery(causeService.getOrganisation, {
    organisationId: organisationId ?? ''
  })
  const causeName = cause?.name
  const causeDisplayName = cause?.displayName
  const causeDescription = cause?.description
  const causeRegistryId = cause?.registryId
  const causeAddress = cause?.address
  const causeLogo = cause?.logo
  const handleClaimStatus = () => {
    if (claimOrganisation?.rejectedAt) {
      return CLAIM_STATUS.rejected
    }

    if (verifiedCauseOwner) {
      return CLAIM_STATUS.verified
    }

    return CLAIM_STATUS.pending
  }
  const claimStatus = handleClaimStatus()

  const { userCan } = usePermissionCheck()
  const canReadWriteBankDetails = userCan(['bank_details:write', 'bank_details:read'])

  const scrollToBankDetails = section === 'bank-details'
  const bankDetailsSectionRef = useScrollToSection(scrollToBankDetails)

  if (isLoading) {
    return <Loader />
  }

  return (
    <div className={verifiedCauseOwner ? styles.verifiedContainer : styles.unVerifiedContainer}>
      <Grid gutter={16}>
        <TitleCard title={t('card.causeDetails')} description={t('card.profileDescription')} size={7}>
          <ProfileForm
            verifiedCauseOwner={verifiedCauseOwner}
            causeId={organisationId}
            causeName={causeName}
            causeDisplayName={causeDisplayName}
            causeDescription={causeDescription}
            refreshApi={refresh}
            causeLogo={causeLogo}
          />
        </TitleCard>
        <TitleCard title={t('card.charityDetails')} size={5}>
          <Flex direction="column" gap={16}>
            <Typography variant="h5" color="secondary">
              {causeName}
            </Typography>
            <Flex direction="column" gap={8}>
              {causeAddress && (
                <Typography color="secondary" className={styles.address}>
                  {causeAddress}
                </Typography>
              )}
              <Typography className={styles.charityNumber}>
                {t('typography.registeredId')}: {causeRegistryId}
              </Typography>
              <div className={styles.status}>
                <Typography>{t('typography.accountStatus')}:</Typography>
                <ClaimStatus claimStatus={claimStatus} />
              </div>
            </Flex>
          </Flex>
        </TitleCard>
        {canReadWriteBankDetails && verifiedCauseOwner && (
          <BankDetails
            verifiedCauseOwner={verifiedCauseOwner}
            organisationId={organisation?.id}
            sectionRef={bankDetailsSectionRef}
          />
        )}
      </Grid>
    </div>
  )
}
